import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import TabSwitcher from "../../../../shared/ui/tabs/TabSwitcher";
import { useMotionSafe } from "../../../../hooks/useMotionSafe";

const PANEL_ID = "therapeutic-plan-panel";
import { Loader2, Trash2, FileText, History, Lightbulb } from "lucide-react";
import Button from "../../../../shared/ui/button/Button";
import TherapeuticPlanViewTimeline from "../../../../modules/therapeutic_plan/ui/components/ViewDetails/TherapeuticPlanViewTimelineForm";
import type { TherapeuticPlan, TherapeuticPhase } from "../../domain/TherapeuticPlan";
import TherapeuticPlanViewBasicForm from "../../../../modules/therapeutic_plan/ui/components/ViewDetails/TherapeuticPlanViewBasicForm";
import { deleteTherapeuticPlan, getTherapeuticPlan, updateTherapeuticPlan } from "../../application/therapeuticPlans";
import PageMeta from "../../../../shared/ui/common/PageMeta";
import PageBreadcrumb from "../../../../shared/ui/common/PageBreadCrumb";
import TherapeuticPlanAIAnalysis from "../../../../modules/therapeutic_plan/ui/components/ViewDetails/TherapeuticPlanAIAnalysis";
import NotionEditor from "../../../../shared/ui/form/input/NotionEditor";
import { useAiInsightsGate } from "../../../ai_insights";


export default function TherapeuticPlan() {
  const { t } = useTranslation();
  const { reduced } = useMotionSafe();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [therapeuticPlan, setTherapeuticPlan] = useState<TherapeuticPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { adopted: aiInsightsAdopted, loading: adoptionLoading } = useAiInsightsGate();

  const [activeTab, setActiveTab] = useState<'details' | 'timeline' | 'analysis'>('details');

  // The analysis tab disappears without the AI Insights tool; fall back to details.
  useEffect(() => {
    if (!adoptionLoading && !aiInsightsAdopted && activeTab === 'analysis') {
      setActiveTab('details');
    }
  }, [adoptionLoading, aiInsightsAdopted, activeTab]);


  const fetchTherapeuticPlan = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const plan = await getTherapeuticPlan(id);
      setTherapeuticPlan(plan);
    } catch (err) {
      console.error("Error fetching therapeutic plan:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTherapeuticPlan();
  }, [fetchTherapeuticPlan]);

  // 🔹 Recalculate order
  const updateTimelineWithOrder = (timeline: TherapeuticPhase[]) => {
    return timeline.map((phase, index) => ({
      ...phase,
      order: index + 1, // order empieza en 1
    }));
  };

  const handleSave = async () => {
    if (!therapeuticPlan) return;
    try {
      setSaving(true);

      // Aseguramos que las fases tengan el order actualizado
      const timelineWithOrder = updateTimelineWithOrder(therapeuticPlan.timeline ?? []);

      await updateTherapeuticPlan(therapeuticPlan.id, {
        ...therapeuticPlan,
        timeline: timelineWithOrder,
      });

      await fetchTherapeuticPlan();
    } catch (err) {
      console.error("Error saving therapeutic plan:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm(t("therapeuticPlan.deleteConfirm"))) return;

    try {
      setDeleting(true);
      await deleteTherapeuticPlan(id);
      navigate("/therapeutic-plans");
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader2 className="animate-spin w-8 h-8 text-gray-500" />
      </div>
    );
  }

  if (!therapeuticPlan) {
    return <div className="p-6 text-center">{t("therapeuticPlan.notFound")}</div>;
  }

  // 🔹 Tabs Configuration
  // The AI analysis tab belongs to the AI Insights tool.
  const tabs = [
    { id: 'details', label: t("therapeuticPlan.tabs.details"), icon: FileText },
    { id: 'timeline', label: t("therapeuticPlan.tabs.timeline"), icon: History },
    ...(adoptionLoading || aiInsightsAdopted
      ? [{ id: 'analysis', label: t("therapeuticPlan.tabs.analysis"), icon: Lightbulb }]
      : []),
  ];


  return (
    <div className="space-y-8">
      <PageMeta />
      <PageBreadcrumb pageTitle={`${therapeuticPlan.title}`} />

      {/* Responsive Navigation */}
      <div className="w-full mb-10 relative z-30">
        <TabSwitcher
          tabs={tabs}
          activeTab={activeTab}
          onChange={(id) => setActiveTab(id as 'details' | 'timeline' | 'analysis')}
          label={t("therapeuticPlan.tabs.label", "Plan sections")}
          layoutId="activeTabPlan"
          panelId={PANEL_ID}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          id={PANEL_ID}
          role="tabpanel"
          initial={{ opacity: 0, y: reduced ? 0 : 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: reduced ? 0 : -6 }}
          transition={{ duration: reduced ? 0.12 : 0.2 }}
        >
          {activeTab === 'details' && (
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">
              {/* Left Column: Side Info */}
              <div className="xl:col-span-4 xl:sticky xl:top-8 space-y-6">
                <TherapeuticPlanViewBasicForm
                  therapeuticPlan={therapeuticPlan}
                  onChange={(updated) => setTherapeuticPlan(prev => prev ? { ...prev, ...updated } : null)}
                />
              </div>

              {/* Right Column: Main Content Area (Spacious Context Editor) */}
              <div className="xl:col-span-8">
                <div className="glass-effect glass-border border rounded-3xl p-8 shadow-theme-sm space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-brand-500/10 text-brand-500 flex items-center justify-center">
                      <FileText className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl font-bold text-content-primary">
                      {t("therapeuticPlan.initialContext")}
                    </h3>
                  </div>

                  <div className="rounded-2xl border border-gray-100 dark:border-white/5 bg-white/40 dark:bg-white/[0.02] overflow-hidden min-h-[300px]">
                    <NotionEditor
                      value={therapeuticPlan.initial_context || ""}
                      onChange={(value: string) =>
                        setTherapeuticPlan(prev => prev ? { ...prev, initial_context: value } : null)
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Bottom Actions Bar */}
              <div className="col-span-full flex flex-row-reverse gap-3 pt-6 border-t mt-4 border-gray-100 dark:border-white/5">
                <Button
                  onClick={handleSave}
                  loading={saving}
                  className="w-full max-w-[200px] shadow-lg shadow-brand-500/20 py-3 font-bold"
                >
                  {saving ? t("therapeuticPlan.saving") : t("therapeuticPlan.saveChanges")}
                </Button>

                <Button
                  variant="outline"
                  className="w-full max-w-[200px] text-red-500 hover:text-red-600 hover:bg-red-50 border-gray-200"
                  onClick={handleDelete}
                  disabled={deleting}
                  startIcon={<Trash2 className="w-4 h-4" />}
                >
                  {t("common.delete")}
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="max-w-5xl mx-auto space-y-8">
              <TherapeuticPlanViewTimeline
                patientId={therapeuticPlan.patient_id || therapeuticPlan.couple_patient_id}
                isCouple={!!therapeuticPlan.couple_patient_id}
                timeline={therapeuticPlan.timeline ?? []}
                onChange={(updatedTimeline) =>
                  setTherapeuticPlan((prev) => prev ? ({
                    ...prev,
                    timeline: updateTimelineWithOrder(updatedTimeline),
                  }) : null)
                }
              />

              {/* Bottom Actions Bar (Parity with Details) */}
              <div className="flex flex-row-reverse gap-3 pt-6 border-t mt-4 border-gray-100 dark:border-white/5">
                <Button
                  onClick={handleSave}
                  loading={saving}
                  className="w-full max-w-[200px] shadow-lg shadow-brand-500/20 py-3 font-bold"
                >
                  {saving ? t("therapeuticPlan.saving") : t("therapeuticPlan.saveChanges")}
                </Button>

                <Button
                  variant="outline"
                  className="w-full max-w-[200px] text-red-500 hover:text-red-600 hover:bg-red-50 border-gray-200"
                  onClick={handleDelete}
                  disabled={deleting}
                  startIcon={<Trash2 className="w-4 h-4" />}
                >
                  {t("common.delete")}
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'analysis' && (
            <div className="max-w-5xl mx-auto">
              <TherapeuticPlanAIAnalysis therapeuticPlan={therapeuticPlan} />
            </div>
          )}
        </motion.div>
      </AnimatePresence>

    </div>
  );
}
